import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Globe } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isStaff } from "@/lib/staff";
import { LANGUAGES, getLang, isLang, marketingPageOf, marketingPath, rememberLang, setLang, t } from "@/i18n";

// Language picker. On marketing pages it moves to the same page's URL in the chosen language;
// elsewhere it stores the choice (and the signed-in user's preference) and reloads.
export function LangSwitcher({ className = "", dark = false, testid = "lang-switcher" }) {
  const loc = useLocation();
  const { user } = useAuth() || {};
  const onChange = async (e) => {
    const l = e.target.value;
    if (!isLang(l) || l === getLang()) return;
    if (user) await api.patch("/auth/prefs", { lang_pref: l }).catch(() => {});
    const mp = marketingPageOf(loc.pathname);
    if (mp) setLang(l, { to: marketingPath(l, mp.page) + loc.hash });
    else setLang(l);
  };
  return (
    <label className={`relative inline-flex items-center gap-1.5 text-sm ${dark ? "text-white/80" : "text-muted-foreground"} ${className}`}>
      <Globe className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="sr-only">{t("Language")}</span>
      <select value={getLang()} onChange={onChange} data-testid={testid}
        className={`cursor-pointer appearance-none rounded-md bg-transparent py-1 pr-1 font-medium outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A0] ${dark ? "text-white" : "text-foreground"}`}>
        {LANGUAGES.map((l) => <option key={l.code} value={l.code} lang={l.code} className="text-black">{l.name}</option>)}
      </select>
    </label>
  );
}

// Keeps the signed-in user's saved language and the page language in step (dashboard and onboarding only).
export function LangSync() {
  const { user } = useAuth() || {};
  const loc = useLocation();
  useEffect(() => {
    if (!user || isStaff(user)) return;
    if (!/^\/(app|onboarding)(\/|$)/.test(loc.pathname)) return;
    if (isLang(user.lang_pref)) {
      if (user.lang_pref !== getLang()) {
        rememberLang(user.lang_pref);
        window.location.reload();
      }
    } else api.patch("/auth/prefs", { lang_pref: getLang() }).catch(() => {});
  }, [user, loc.pathname]);
  return null;
}
